import React, { useState, useEffect } from 'react';
import { FiImage, FiDownload, FiPlus, FiAlertCircle, FiFile, FiChevronDown } from 'react-icons/fi';
import { downloadImage, getFilenameFromPath, downloadBlob } from '../services/api';
import './Result.css';

/**
 * Result Component
 * Displays processed image with download options (PNG, JPG, PDF)
 */
const Result = ({ resultImageUrl, onReset, outputFilename }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [jpegQuality, setJpegQuality] = useState(95);

  // Reset state when resultImageUrl changes
  useEffect(() => {
    if (resultImageUrl) {
      setImageLoading(true);
      setImageError(false);
    }
  }, [resultImageUrl]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDownloadMenu && !e.target.closest('.download-menu-container')) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  if (!resultImageUrl) {
    return null;
  }

  // Add cache-busting parameter
  const imageUrlWithCache = resultImageUrl + (resultImageUrl.includes('?') ? '&' : '?') + `t=${Date.now()}`;

  // Get filename from URL or prop
  const filename = outputFilename || getFilenameFromPath(resultImageUrl);

  const handleDownload = async (format = 'png') => {
    setDownloading(true);
    setShowDownloadMenu(false);
    
    try {
      // Try to use the API download endpoint for format conversion
      if (filename) {
        const blob = await downloadImage(filename, format, jpegQuality, 'A4');
        const extension = format === 'jpeg' ? 'jpg' : format;
        downloadBlob(blob, `processed-${Date.now()}.${extension}`);
      } else {
        // Fallback: direct download from URL
        const response = await fetch(resultImageUrl);
        const blob = await response.blob();
        downloadBlob(blob, `processed-${Date.now()}.png`);
      }
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: open in new tab
      window.open(resultImageUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  const downloadFormats = [
    { id: 'png', label: 'PNG', icon: '🖼️', desc: 'Best quality, transparent' },
    { id: 'jpg', label: 'JPG', icon: '📷', desc: 'Smaller file size' },
    { id: 'pdf', label: 'PDF', icon: '📄', desc: 'Print-ready document' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 px-2 gap-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FiImage className="text-blue-600" /> Processed Result
        </h2>
        
        <div className="flex gap-2 w-full sm:w-auto">
          {/* Download Button with Dropdown */}
          <div className="relative download-menu-container flex-1 sm:flex-none">
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={downloading}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-lg transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {downloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <FiDownload /> Download <FiChevronDown className="ml-1" />
                </>
              )}
            </button>

            {/* Download Dropdown Menu */}
            {showDownloadMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Download Format</p>
                </div>
                
                {downloadFormats.map((format) => (
                  <button
                    key={format.id}
                    onClick={() => handleDownload(format.id)}
                    className="w-full px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-3 transition-all text-left"
                  >
                    <span className="text-xl">{format.icon}</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{format.label}</p>
                      <p className="text-xs text-gray-500">{format.desc}</p>
                    </div>
                  </button>
                ))}

                {/* JPEG Quality Slider (only show for JPG) */}
                <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-slate-900">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-500">JPG Quality</span>
                    <span className="text-xs font-bold text-blue-600">{jpegQuality}%</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="100"
                    value={jpegQuality}
                    onChange={(e) => setJpegQuality(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                </div>
              </div>
            )}
          </div>

          {/* New Image Button */}
          <button
            onClick={onReset}
            className="flex-1 sm:flex-none px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <FiPlus /> New
          </button>
        </div>
      </div>

      {/* Image Display */}
      <div className="flex-1 relative bg-gray-100 dark:bg-slate-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 flex items-center justify-center min-h-[220px]">
        {/* Checkerboard background for transparency */}
        <div 
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                             linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                             linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                             linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)`,
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
          }}
        />
        
        {imageLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50/80 dark:bg-slate-900/80 z-10">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
            <p className="text-sm font-medium text-gray-500">Optimizing result...</p>
          </div>
        )}

        {imageError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-20 bg-white/90 dark:bg-slate-900/90">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-4 text-2xl">
              <FiAlertCircle />
            </div>
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">Failed to Load Image</h4>
            <p className="text-sm text-gray-500 mb-4 max-w-xs break-all">{resultImageUrl}</p>
            <button
              onClick={() => { setImageLoading(true); setImageError(false); }}
              className="text-sm text-blue-600 font-bold hover:underline"
            >
              Retry Loading
            </button>
          </div>
        )}

        <img
          key={imageUrlWithCache}
          src={imageUrlWithCache}
          alt="Processed"
          className={`relative z-10 max-w-full max-h-full object-contain transition-opacity duration-500 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageLoading(false);
            setImageError(true);
          }}
        />
      </div>

      {/* Quick Actions */}
      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          onClick={() => handleDownload('png')}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-all flex items-center gap-1"
        >
          <FiDownload size={12} /> Quick PNG
        </button>
        <button
          onClick={() => handleDownload('jpg')}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-all flex items-center gap-1"
        >
          <FiDownload size={12} /> Quick JPG
        </button>
        <button
          onClick={() => handleDownload('pdf')}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-all flex items-center gap-1"
        >
          <FiFile size={12} /> PDF
        </button>
      </div>
    </div>
  );
};

export default Result;
